/// <amd-module name="Wit/WorkItemForm/Common/Hooks/useSet" />
import { useState, useCallback} from "react";

/**
 * A hook which provides functions for interacting with a stateful Set.
 * The state will change (and re-render the component) when the contents of the set change.
 * 
 * @param initialSet
 * @returns [contains, add, delete]: functions for inspecting and updating the Set
 */
export function useSet<T>(initialSet?: Set<T>): [(item: T) => boolean, (item: T) => void, (item: T) => void] {
    const [contents, setContents] = useState(initialSet ?? new Set<T>());

    const contains = useCallback(
        (item: T) => {
            return contents.has(item);
        },
        [contents]
    );

    const add = useCallback(
        (item: T) => {
            const newContents = new Set(contents);
            newContents.add(item);
            setContents(newContents);
        },
        [contents]
    );

    const remove = useCallback(
        (item: T) => {
            const newContents = new Set(contents);
            newContents.delete(item);
            setContents(newContents);
        },
        [contents]
    );

    return [contains, add, remove];
}
